import { useState } from 'react';
import { t } from '@bms2/shared';
import { TextInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { post, DEALS_PATH } from './api';

/** 🔨 Relative to the API's own root, never prefixed with /api: + New on the Bills of Materials list (decision 1092). */
export const BOMS_PATH = `${DEALS_PATH}/boms`;

export interface NewBomModalProps {
  apiBaseUrl?: string;
  onCancel: () => void;
  /** The list made, attached to no Deal; the caller opens it. */
  onCreated: (made: { id: string }) => void;
}

/**
 * 🔒 + New on the Bills of Materials list (decision 1092): the name and the
 * description, as the Deal's Add asks them, making a list attached to no Deal.
 * 🔨 The caller unmounts it when closed, so a second + New opens on empty
 * fields rather than on what was just saved.
 */
export function NewBomModal({ apiBaseUrl = '', onCancel, onCreated }: NewBomModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setError(null);
    const wanted = name.trim();
    if (!wanted) return setError(t('deal.boms.needsName'));
    setSaving(true);
    try {
      const answer = await post<{ id: string }>(BOMS_PATH, { name: wanted, description: description.trim() || null }, apiBaseUrl);
      if (!answer.ok) return setError(answer.message);
      onCreated({ id: answer.data.id });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open
      title={t('deal.boms.new')}
      onCancel={onCancel}
      dataModal="bom-new"
      actions={[
        { kind: 'primary', label: t('modal.save'), onAct: () => void save() },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <TextInput chars={32} label={t('deal.boms.name')} value={name} onChange={setName} name="bomName" disabled={saving} />
      <TextInput chars={60} label={t('deal.boms.description')} value={description} onChange={setDescription} placeholder={t('deal.boms.descriptionPlaceholder')} name="bomDescription" disabled={saving} />
    </Modal>
  );
}
